import { useState, useEffect } from "react";
import { ref, onValue, off, update, push, get } from "firebase/database";
import { db, app } from "../../../configs/Firebase";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { useSelector } from "react-redux";
import style from "./Scoring.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import ScoringEvaluationDialog from "../components/ScoringEvaluationDialog";
import ScoringSummaryDialog from "../components/ScoringSummaryDialog";
import {
  getAggregatedStepScore,
  calculateStepScore,
} from "../utils/scoringUtils";

const Scoring = (props) => {
  const userStatus = useSelector((state) => state.userStatus.value);
  const [user, setUser] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState(null);
  const [aggregatedScore, setAggregatedScore] = useState(null);
  const [pictureURL, setPictureURL] = useState(null);
  const org = useSelector((state) => state.orgStatus.value);

  useEffect(() => {
    if (userStatus && userStatus.payload) {
      const userData = JSON.parse(userStatus.payload);
      setUser(userData);
    }
  }, [userStatus]);

  // Load evaluations from Firebase
  useEffect(() => {
    if (!props.journeyRefURL || !props.data.id || !props.row.id) return;

    const scoringPath = `${props.journeyRefURL}/scoring/${props.row.id}/${props.data.id}/evaluations`;
    const evaluationsRef = ref(db, scoringPath);

    const unsubscribe = onValue(evaluationsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const evaluationsList = [];
        for (const evalId in data) {
          evaluationsList.push({
            id: evalId,
            ...data[evalId],
          });
        }
        setEvaluations(evaluationsList);

        // Find existing evaluation for current user
        if (user && user.uid) {
          const userEval = evaluationsList.find(
            (evaluation) => evaluation.designerId === user.uid
          );
          setExistingEvaluation(userEval || null);
        }
      } else {
        setEvaluations([]);
        setExistingEvaluation(null);
      }
    });

    return () => {
      off(evaluationsRef);
      unsubscribe();
    };
  }, [props.journeyRefURL, props.data.id, props.row.id, user]);

  // Calculate aggregated score
  useEffect(() => {
    if (evaluations.length > 0) {
      const aggregated = getAggregatedStepScore(evaluations);
      setAggregatedScore(aggregated);
    } else {
      setAggregatedScore(null);
    }
  }, [evaluations]);

  // Find and fetch picture from same column index in picture-type rows
  useEffect(() => {
    if (!props.journeyRefURL || !org?.payload || props.cellIndex === undefined) {
      setPictureURL(null);
      return;
    }

    const journeyRef = ref(db, props.journeyRefURL);
    
    get(journeyRef).then((snapshot) => {
      if (!snapshot.exists()) {
        setPictureURL(null);
        return;
      }

      const journeyData = snapshot.val();
      const rows = journeyData.rows || {};
      
      // Find picture rows
      let pictureFound = false;
      for (const rowId in rows) {
        const row = rows[rowId];
        if (row.type === "picture" && row.columns) {
          // Check if this row has a column at the same index
          let columnsArray = [];
          if (Array.isArray(row.columns)) {
            columnsArray = row.columns;
          } else {
            // Convert object to array
            for (const colKey in row.columns) {
              columnsArray.push({
                ...row.columns[colKey],
                id: row.columns[colKey].id || colKey
              });
            }
            // Sort by displayOrder
            columnsArray.sort((a, b) => {
              const orderA = a.displayOrder !== undefined ? a.displayOrder : parseInt(a.id);
              const orderB = b.displayOrder !== undefined ? b.displayOrder : parseInt(b.id);
              return orderA - orderB;
            });
          }

          // Check if column exists at cellIndex
          if (columnsArray[props.cellIndex] && columnsArray[props.cellIndex].value) {
            const filename = columnsArray[props.cellIndex].value;
            const storage = getStorage(app);
            const imageRef = storageRef(storage, `${org.payload}/images/${filename}`);
            
            getDownloadURL(imageRef)
              .then((url) => {
                setPictureURL(url);
                pictureFound = true;
              })
              .catch(() => {
                // Picture not found or error loading
                if (!pictureFound) {
                  setPictureURL(null);
                }
              });
            break; // Found a picture, stop searching
          }
        }
      }
      
      if (!pictureFound) {
        setPictureURL(null);
      }
    }).catch(() => {
      setPictureURL(null);
    });
  }, [props.journeyRefURL, props.cellIndex, org?.payload]);

  function handleCellClick() {
    if (props.editable && user) {
      // Edit mode: show evaluation dialog
      setShowDialog(true);
    } else if (!props.editable) {
      // Non-edit mode: show summary modal
      setShowSummaryDialog(true);
    }
  }

  function handleSaveEvaluation(evaluation) {
    if (!props.journeyRefURL || !props.data.id || !props.row.id || !user) return;

    const scoringPath = `${props.journeyRefURL}/scoring/${props.row.id}/${props.data.id}/evaluations`;
    const evaluationsRef = ref(db, scoringPath);

    // Calculate stepScore for this evaluation
    const stepScore = calculateStepScore(evaluation);

    const evaluationData = {
      ...evaluation,
      stepScore,
      createdAt: existingEvaluation?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    if (existingEvaluation) {
      // Update existing evaluation
      update(ref(db, `${scoringPath}/${existingEvaluation.id}`), evaluationData);
    } else {
      // Create new evaluation
      push(evaluationsRef, evaluationData);
    }
  }

  function getScoreColor(percentage) {
    if (percentage === null || percentage === undefined) return "neutral";
    if (percentage >= 80) return "high";
    if (percentage >= 60) return "medium";
    return "low";
  }

  function getColor(value) {
    // Convert percentage (0-100) to 0-1 range for color calculation
    const normalizedValue = value / 100;
    const hue = normalizedValue * 120;
    return `hsl(${hue}, 65%, 70%)`;
  }

  const scorePercentage = aggregatedScore?.stepScoreFinalPercentage || null;
  const hasHighVariance = aggregatedScore?.hasHighVariance || false;
  const evaluationCount = aggregatedScore?.evaluationCount || 0;

  return (
    <>
      <div
        className={`${style.cell} ${props.editable ? style.editable : ""} ${
          style[getScoreColor(scorePercentage)]
        }`}
        onClick={handleCellClick}
      >
        <div className={style.scoreDisplay}>
          {scorePercentage !== null ? (
            <>
              <div className={style.scoreValue}>
                {Math.round(scorePercentage)}%
              </div>
              <div className={style.scoreBar}>
                <div
                  className={style.scoreBarFill}
                  style={{ 
                    width: `${scorePercentage}%`,
                    backgroundColor: getColor(scorePercentage)
                  }}
                />
              </div>
            </>
          ) : (
            <div className={style.noScore}>
              {evaluationCount === 0
                ? "Click to evaluate"
                : "No score available"}
            </div>
          )}
        </div>
        {hasHighVariance && (
          <div className={style.varianceWarning} title="High variance in scores">
            <Icon>warning</Icon>
          </div>
        )}
        {evaluationCount > 0 && (
          <div className={style.evaluationCount}>
            {evaluationCount} {evaluationCount === 1 ? "evaluation" : "evaluations"}
          </div>
        )}
      </div>

      <ScoringEvaluationDialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        cellId={props.data.id}
        rowId={props.row.id}
        journeyRefURL={props.journeyRefURL}
        userId={user?.uid}
        userName={user?.displayName || user?.email || "Designer"}
        existingEvaluation={existingEvaluation}
        onSave={handleSaveEvaluation}
        pictureURL={pictureURL}
      />

      <ScoringSummaryDialog
        show={showSummaryDialog}
        onClose={() => setShowSummaryDialog(false)}
        evaluations={evaluations}
        aggregatedScore={aggregatedScore}
        pictureURL={pictureURL}
      />
    </>
  );
};

export default Scoring;

